import React, { useState } from 'react'
import { IResponse } from './TemplateMovementsExport.types'
import { useHistory, useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alert';
import { useAuth } from 'contexts/auth';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, Chip, Table, TableCell, TableContainer, TableRow, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, Alert, Typography, Box, CircularProgress, Tooltip } from '@mui/material';
import { formatActivityStatus, formatDate, formatMovementType, formatStatus, formatType } from './TemplateMovementsExport.utils';
import { cpfMask } from 'utils/cpf';
import { Metrics, Modal } from 'v2/components';
import { Label, TableLabel, TableText, Title } from './TemplateMovementsExport.styled';
import { Close, Block as BlockIcon, VerifiedOutlined as VerifiedIcon, ArrowBack, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { ptBR } from '@mui/x-data-grid/locales';
import api from 'services/api';
import dayjs from 'dayjs';

export function TemplateMovementsExportView({ data, productType }: { data: IResponse, productType: string }) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string };
  const { openAlert } = useAlert();
  const user = useAuth();
  const history = useHistory();

  const [currentStep, setCurrentStep] = useState<"DEFAULT" | "RECTIFY">("DEFAULT");
  const [isModalActivityOpen, setIsModalActivityOpen] = useState(false);
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalRetainReleaseOpen, setIsModalRetainReleaseOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);
  const [retentionReason, setRetentionReason] = useState<string>('');
  const [retentionNotes, setRetentionNotes] = useState<string>('');
  const [modifiedRows, setModifiedRows] = useState<Map<string, { retentionReason: string; retentionNotes: string; newStatus: string }>>(new Map());

  const rows = data.people.map((person, index) => {
    const modification = modifiedRows.get(person.id);
    const currentStatus = modification ? modification.newStatus : person.status;

    return {
      id: person.id || `${index + 1}_${Date.now()}`,
      name: person.name || '-',
      taxPayerId: person.taxPayerId ? cpfMask(person.taxPayerId) : '-',
      taxPayerIdEmployee: person.taxPayerIdEmployee ? cpfMask(person.taxPayerIdEmployee) : '-',
      status: currentStatus,
      movementType: formatMovementType(person.movementType),
      type: formatType(person.type),
      plan: person.plan ?? '-',
      vigencyStartDate: formatDate(String(person.vigencyStartDate)),
      vigencyEndDate: formatDate(String(person.vigencyEndDate)),
      hiringDate: formatDate(String(person.hiringDate)),
      dismissalDate: formatDate(String(person.dismissalDate)),
      startPriorNoticeDate: person.startPriorNoticeDate,
      endPriorNoticeDate: person.endPriorNoticeDate,
      exclusionDateType: person.exclusionDateType,
      effectiveExclusionDate: person.effectiveExclusionDate,
      role: person.role ?? '-',
      city: person.city ?? '-',
      retentionReason: person.retentionReason ?? null,
      retentionNotes: person.retentionNotes ?? null,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nome Completo',
      width: 300,
      sortable: true,
    },
    {
      field: 'taxPayerId',
      headerName: 'CPF do Beneficiário',
      width: 160,
      sortable: true,
    },
    {
      field: 'taxPayerIdEmployee',
      headerName: 'CPF do Titular',
      width: 160,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      type: 'singleSelect',
      valueOptions: [
        { value: 'READY', label: 'Pronto' },
        { value: 'SKIPED', label: 'Retido' },
        { value: 'DELAYED', label: 'Em espera' },
        { value: 'RETAINED', label: 'Retificar' },
      ], // {value, label} pairs
      renderCell: (params: GridRenderCellParams) => {
        const { label, backgroundColor, color } = formatStatus(params.row.status);
        return (
          <Chip
            label={label}
            size="small"
            sx={{ backgroundColor: backgroundColor, color: color, fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'movementType',
      headerName: 'Tipo de movimentação',
      width: 160,
      sortable: true,
    },
    {
      field: 'type',
      headerName: 'Tipo do Beneficiário',
      width: 150,
      sortable: true,
    },
    {
      field: 'plan',
      headerName: 'Plano',
      width: 200,
      sortable: true,
    },
    {
      field: 'vigencyStartDate',
      headerName: 'Data inicio plano',
      width: 180,
      sortable: true,
    },
    {
      field: 'vigencyEndDate',
      headerName: 'Data fim plano',
      width: 180,
      sortable: true,
    },
    {
      field: 'hiringDate',
      headerName: 'Data de admissão',
      width: 180,
      sortable: true,
    },
    {
      field: 'dismissalDate',
      headerName: 'Data de demissão',
      width: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value || params.value === '-') return '-';
        const isUsed = params.row.exclusionDateType === 'DISMISSAL_DATE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {params.value}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'endPriorNoticeDate',
      headerName: 'Término Aviso Prévio',
      width: 220,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.endPriorNoticeDate) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        const isUsed = params.row.exclusionDateType === 'PRIOR_NOTICE_END_DATE';
        const formattedDate = formatDate(String(params.row.endPriorNoticeDate));
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {formattedDate}
            </Typography>
            {isUsed && (
              <Tooltip title="Esta data está sendo usada para calcular exclusões">
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'role',
      headerName: 'Cargo',
      width: 180,
      sortable: true,
    },
    {
      field: 'city',
      headerName: 'Cidade',
      width: 180,
      sortable: true,
    },
    {
      field: "retainRelease",
      headerName: "Reter/Liberar",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const isRetained = params.row.status === 'RETAINED';

        return (
          <IconButton
            size="small"
            onClick={() => handleOpenRetainReleaseModal(params.row)}
            sx={{
              color: isRetained ? '#2E7D32' : '#C62828'
            }}
          >
            {isRetained ? (
              <VerifiedIcon />
            ) : (
              <BlockIcon />
            )}
          </IconButton>
        );
      },
    },
  ];

  async function handleExport() {
    try {
      setIsExporting(true);

      const beingExported = [ ...data.people ];
      const metrics = new Array();
      metrics.push({ label: "Total", value: String(beingExported?.length ?? 0) });
      metrics.push({ label: "Inclusões", value: String(beingExported?.filter(p => p?.movementType === "inclusion")?.length ?? 0) });
      metrics.push({ label: "Exclusões", value: String(beingExported?.filter(p => p?.movementType === "exclusion")?.length ?? 0) });
      metrics.push({ label: "Alterações", value: String(beingExported?.filter(p => p?.movementType === "change")?.length ?? 0) });
      metrics.push({ label: "Transferências", value: String(beingExported?.filter(p => p?.movementType === "transfer")?.length ?? 0) });

      const exportWorkspaceActivityId = data.activities.find(a => a.slug === "app_export")?.workspaceActivityId ?? null;

      if (!exportWorkspaceActivityId) throw new Error("Workspace Activity ID de exportação é obrigatório.");

      await api.post(`/v2/export/${exportWorkspaceActivityId}`);

      openAlert('Massa movimentada exportada com sucesso.', 'success');
      setIsModalExportOpen(false);
    } catch (error) {
      openAlert('Erro ao exportar massa movimentada.', 'error');
    } finally {
      setIsExporting(false);
    }
  }

  const handleOpenRetainReleaseModal = (beneficiary: any) => {
    setSelectedBeneficiary(beneficiary);

    // Se o beneficiário está RETAINED, buscar informações salvas.
    // Prioriza modificações em memória (edição não-salva); se não houver, usa
    // os dados persistidos no próprio beneficiário (gravados pelo backend, ex: rejeições da API QBem).
    const isRetained = beneficiary?.status === 'RETAINED';
    const modification = modifiedRows.get(beneficiary.id);

    if (isRetained) {
      setRetentionReason(modification?.retentionReason ?? beneficiary?.retentionReason ?? '');
      setRetentionNotes(modification?.retentionNotes ?? beneficiary?.retentionNotes ?? '');
    } else {
      setRetentionReason('');
      setRetentionNotes('');
    }
    setIsModalRetainReleaseOpen(true);
  };

  const handleCloseRetainReleaseModal = () => {
    setIsModalRetainReleaseOpen(false);
    setSelectedBeneficiary(null);
    setRetentionReason('');
    setRetentionNotes('');
  };

  const handleConfirmRetainRelease = () => {
    const beneficiaryId = selectedBeneficiary?.id;
    if (!beneficiaryId) return;

    const isRetained = selectedBeneficiary.status === 'RETAINED';

    // Atualizar o mapa de linhas modificadas
    setModifiedRows(prev => {
      const newMap = new Map(prev);

      if (isRetained) {
        // Liberar: mudar status para DONE e limpar informações
        newMap.set(beneficiaryId, {
          retentionReason: '',
          retentionNotes: '',
          newStatus: 'DONE'
        });
      } else {
        // Reter: mudar status para RETAINED e salvar informações
        newMap.set(beneficiaryId, {
          retentionReason,
          retentionNotes,
          newStatus: 'RETAINED'
        });
      }

      return newMap;
    });

    // Fechar modal
    handleCloseRetainReleaseModal();
  };

  const handleClearAll = () => {
    // Limpar todas as modificações
    setModifiedRows(new Map());
  };

  const handleSubmitRectifications = async () => {
    try {
      if (modifiedRows.size === 0) {
        openAlert('Nenhuma modificação para enviar.', 'warning');
        return;
      }

      setIsExporting(true);

      // Preparar payload com as modificações
      const payload = Array.from(modifiedRows.entries()).map(([beneficiaryId, modification]) => {
        const person = data.people.find(p => p.id === beneficiaryId);

        if (!person) return null;

        return {
          dagRunId: person.dagRunId,
          taxPayerId: person.taxPayerId,
          status: modification.newStatus,
          retentionReason: modification?.retentionReason || null,
          retentionNotes: modification?.retentionNotes || null,
        };
      }).filter(item => item !== null);

      const rectifyWorkspaceActivityId = data.activities.find(a => a.slug === "app_rectify")?.workspaceActivityId;
      await api.post(`/v2/apps/rectification/${rectifyWorkspaceActivityId}`, { payload });

      openAlert('Retificações enviadas com sucesso!', 'success');
      window.location.reload()
    } catch (error) {
      openAlert('Erro ao enviar retificações.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {currentStep === "DEFAULT" && (
        <>
          <Modal open={isModalActivityOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Atividades relacionadas</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalActivityOpen(false)}
              />
            </div>
            <TableText>
              Para ter Exportar Movimentações atualizada verifique se as
              atividades abaixo estão atualizadas de acordo com sua execução
              programada.
            </TableText>
            <div style={{ width: "100%", maxHeight: "400px", overflow: "scroll" }}>
              <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
                <Table>
                  <TableRow style={{ height: "70px" }}>
                    <TableCell>
                      <TableLabel>Nome</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Última atualização</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Execução programada</TableLabel>
                    </TableCell>
                    <TableCell>
                      <TableLabel>Status</TableLabel>
                    </TableCell>
                  </TableRow>
                  {data.activities.map(activity => (
                    <TableRow key={activity.slug} style={{ height: "70px" }}>
                      <TableCell>{activity.name}</TableCell>
                      <TableCell>{formatDate(String(activity.lastExecution), "timestamp_2")}</TableCell>
                      <TableCell>{formatDate(String(activity.scheduledExecution), "timestamp_2")}</TableCell>
                      <TableCell>
                        <Chip
                          label={formatActivityStatus(activity.status).label}
                          size="small"
                          sx={{ backgroundColor: formatActivityStatus(activity.status).backgroundColor, color: formatActivityStatus(activity.status).color, fontWeight: "500", opacity: "0.8" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </div>
          </Modal>
          <Modal open={isModalExportOpen} size="lg">
            <div style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <Title>Exportar movimentações?</Title>
              <Close
                style={{ cursor: "pointer" }}
                onClick={() => setIsModalExportOpen(false)}
              />
            </div>
            <TableText>
              Você está prestes a exportar as movimentações
            </TableText>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
              <Button
                variant="outlined"
                color="error"
                style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                disabled={isExporting}
                onClick={() => setIsModalExportOpen(false)}
              >
                Voltar para revisão
              </Button>
              <Button
                variant='contained'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                disabled={isExporting}
                onClick={handleExport}
              >
                Exportar
              </Button>
            </div>
          </Modal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title>Exportar movimentações</Title>
            <Button
              variant="outlined"
              style={{ padding: "1px 16px 1px 16px", fontSize: "14px", fontWeight: "600" }}
              onClick={() => setIsModalActivityOpen(true)}
            >
              Atividades relacionadas
            </Button>
          </div>
          <div style={{ display: "flex", flexDirection: "row", gap: "16px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Última aprovação em</Label>
              <Label>
                <strong>{formatDate(String(data.lastExecution), "timestamp")}</strong>
              </Label>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "center" }}>
              <Label>Status</Label>
              <Chip
                label={formatActivityStatus(data.status).label}
                size="small"
                sx={{ backgroundColor: formatActivityStatus(data.status).backgroundColor, color: formatActivityStatus(data.status).color, fontWeight: "500", opacity: "0.8" }}
              />
            </div>
          </div>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px" }}>
            <Metrics title="Movimentações" metrics={data.metricsMovement.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
            <Metrics title="Detalhamento por status" metrics={data.metricsStatus.map(m => m.label === "Total" ? { ...m, bold: true } : m)} />
          </div>
          <DataGrid
            rows={rows}
            columns={columns.filter(c => c?.field !== "retainRelease")}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            <Button
              variant='outlined'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setCurrentStep("RECTIFY")}
            >
              Retificar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalExportOpen(true)}
            >
              Exportar movimentações
            </Button>
          </div>
        </>
      )}
      {currentStep === "RECTIFY" && (
        <>
          <Modal open={isModalRetainReleaseOpen} size="lg">
            <Box sx={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Title>{selectedBeneficiary?.status === 'RETAINED' ? "Liberar beneficiário" : "Reter beneficiário"}</Title>
                <Close
                  style={{ cursor: "pointer" }}
                  onClick={handleCloseRetainReleaseModal}
                />
              </div>
              <Typography variant="body1" style={{ marginBottom: "20px", marginTop: "5px" }}>
                {selectedBeneficiary?.status === 'RETAINED'
                  ? 'Ao liberar o beneficiário ele vai ficar com o status de "Aprovado" e pode ser movimentado'
                  : 'Ao acionar o status de "Retificar" o beneficiário fica impossibilitado de ser movimentado até que se resolva a pendência abaixo'}
              </Typography>
              <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: "space-between", marginBottom: "20px" }}>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Nome</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.name}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do beneficiário</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerId}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CPF do titular</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{selectedBeneficiary?.taxPayerIdEmployee}</span>
                </div>
              </div>
              {selectedBeneficiary?.status === 'RETAINED' && (
                <Alert
                  severity='info'
                  variant='standard'
                  style={{ marginBottom: "20px" }}
                >
                  As pendências abaixo foram resolvidas? caso sim clique em "Liberar beneficiário"
                </Alert>
              )}

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="retention-reason-label">Motivo</InputLabel>
                <Select
                  labelId="retention-reason-label"
                  value={retentionReason}
                  label="Motivo"
                  onChange={(e) => setRetentionReason(e.target.value)}
                  disabled={selectedBeneficiary?.status === 'RETAINED'}
                >
                  <MenuItem value="Documentação Pendente">Documentação Pendente</MenuItem>
                  <MenuItem value="Dados Incorretos">Dados Incorretos</MenuItem>
                  <MenuItem value="Aguardando Aprovação">Aguardando Aprovação</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                label="Observações"
                value={retentionNotes}
                onChange={(e) => setRetentionNotes(e.target.value)}
                disabled={selectedBeneficiary?.status === 'RETAINED'}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    height: '240px',
                    alignItems: 'flex-start',
                  }
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant='outlined'
                  color='error'
                  style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                  onClick={handleCloseRetainReleaseModal}
                >
                  Cancelar
                </Button>

                <Button
                  variant='contained'
                  style={{ minWidth: "180px", paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
                  disabled={
                    // Se está liberando (status RETAINED ou SKIPED), não desabilita
                    // Se está retendo (status diferente), desabilita se não tiver motivo OU observação
                    !(selectedBeneficiary?.status === 'RETAINED' || selectedBeneficiary?.status === 'SKIPED') &&
                    (!retentionReason.trim() || !retentionNotes.trim())
                  }
                  onClick={handleConfirmRetainRelease}
                >
                  {selectedBeneficiary?.status === 'RETAINED'
                    ? 'Liberar beneficiário'
                    : 'Reter beneficiário'}
                </Button>
              </Box>
            </Box>
          </Modal>
          <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => setCurrentStep("DEFAULT")}
            >
              <ArrowBack color="primary" />
            </IconButton>
            <Title>Retificar movimentações</Title>
          </div>
          <div style={{"display":"flex","gap":"1rem"}}>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Total</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Inclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "inclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Exclusões</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "exclusion")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Alterações</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "change")?.length ?? 0}</span>
            </div>
            <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Transferências</p>
              <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{data.people.filter(p => p?.status === "DONE" || p?.status === "RETAINED")?.filter(p => p?.movementType === "transfer")?.length ?? 0}</span>
            </div>
          </div>
          <DataGrid
            rows={rows.filter(r => r.status === "DONE" || r.status === "RETAINED")}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            style={{ backgroundColor: "white", minHeight: 400 }}
          />
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: '20px',"paddingTop":"0.5rem","paddingBottom":"0.5rem" }}>
            <Button
              variant="outlined"
              style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", paddingLeft: "1rem", paddingRight: "1rem", borderRadius: "0.375rem", height: "2.5rem", fontSize: "0.875rem", lineHeight: "1.25rem", fontWeight: 600 }}
              onClick={handleClearAll}
              disabled={modifiedRows.size === 0}
            >
              Limpar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={handleSubmitRectifications}
              disabled={isExporting || modifiedRows.size === 0}
            >
              {!isExporting ? "Salvar" : <CircularProgress size="20px"/>}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
